import React, { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Loader2, CheckCircle2, XCircle, ArrowRight } from 'lucide-react';
import { verifyEmail } from '../api/api';
import { useLanguage } from '../contexts/LanguageContext';
import SEO from '../components/common/SEO';

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [status, setStatus] = useState('loading'); // loading, success, error
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMsg(t('invalidToken', 'Invalid or missing verification token.'));
      return;
    }

    const verify = async () => {
      try {
        await verifyEmail(token);
        setStatus('success');
      } catch (error) {
        setStatus('error');
        setErrorMsg(error.message || t('verificationFailed', 'Verification failed. The token may have expired.'));
      }
    };

    verify();
  }, [token, t]);

  return (
    <>
      <SEO title={t('verifyEmail', 'Verify Email')} />
      <div className="min-h-screen flex items-center justify-center p-4 pt-24">
        <motion.div 
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className="bg-card w-full max-w-md rounded-[2.5rem] shadow-2xl p-8 border border-border text-center relative z-10"
        >
          {status === 'loading' && (
            <div className="flex flex-col items-center space-y-4 py-8">
              <Loader2 className="w-16 h-16 text-primary animate-spin" />
              <h2 className="text-2xl font-black uppercase tracking-tighter">
                {t('verifying', 'Verifying Email...')}
              </h2>
              <p className="text-muted-foreground text-sm">
                {t('pleaseWait', 'Please wait while we verify your email address.')}
              </p>
            </div>
          )}

          {status === 'success' && (
            <div className="flex flex-col items-center space-y-6 py-4">
              <div className="w-20 h-20 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mb-2">
                <CheckCircle2 className="w-10 h-10" />
              </div>
              <h2 className="text-3xl font-black uppercase tracking-tighter text-foreground">
                {t('verified', 'Verified!')}
              </h2>
              <p className="text-muted-foreground text-sm leading-relaxed">
                {t('verifiedDesc', 'Your email address has been successfully verified. You now have full access to all features on Ahlafot.')}
              </p>
              <Link 
                to="/"
                className="w-full bg-primary text-primary-foreground py-4 rounded-2xl font-black uppercase tracking-widest text-sm shadow-xl shadow-primary/20 hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center gap-2 mt-4"
              >
                {t('goToHome', 'Go to Home')}
                <ArrowRight size={18} />
              </Link>
            </div>
          )}

          {status === 'error' && (
            <div className="flex flex-col items-center space-y-6 py-4">
              <div className="w-20 h-20 bg-red-500/20 text-red-500 rounded-full flex items-center justify-center mb-2">
                <XCircle className="w-10 h-10" />
              </div>
              <h2 className="text-3xl font-black uppercase tracking-tighter text-foreground">
                {t('verificationFailedTitle', 'Verification Failed')}
              </h2>
              <p className="text-muted-foreground text-sm leading-relaxed bg-red-500/10 p-4 rounded-2xl text-red-500 font-bold border border-red-500/20 w-full">
                {errorMsg}
              </p>
              <Link 
                to="/"
                className="w-full bg-muted text-foreground py-4 rounded-2xl font-black uppercase tracking-widest text-sm hover:scale-[1.02] active:scale-[0.98] transition-all flex items-center justify-center mt-4"
              >
                {t('goToHome', 'Go to Home')}
              </Link>
            </div>
          )}
        </motion.div>
      </div>
    </>
  );
}
